import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { Controller, FieldValues } from "react-hook-form";

import { DisplayOnlyField } from "./DisplayOnlyField";
import { DynamicFieldProps } from "./DynamicField";
import { ErrorDisplay } from "./ErrorDisplay";

/**
 * Bespoke renderer for the RCRA "State Activities" (§11e) state field. It's a
 * multiselect, but its options are numerous and have long, often paragraph-length
 * descriptions that read poorly in the generic `FormMultiselect`. This component
 * renders its own dropdown so that behavior stays out of the shared component:
 *   - an explanatory caption above the field (otherwise it's just a label + a list
 *     of cryptic, state-specific codes),
 *   - an "N selected" summary in the closed state rather than a comma-joined run-on,
 *   - each long option label truncated with an ellipsis, full text on hover.
 *
 * Wired in via the `fieldOverrides` map in `FacilityStateFieldsForm`.
 */
const CAPTION =
  "State-specific RCRA activities your state tracks in addition to the federal hazardous waste " +
  "activities. Select any that apply to this facility; the available options are specific to " +
  "your state. (§11e)";

/** Max characters shown for a long option label before truncating. */
const MAX_OPTION_LABEL_LENGTH = 64;

/** Truncate at the last word boundary within the cap and append an ellipsis. */
function truncateLabel(label: string): string {
  if (label.length <= MAX_OPTION_LABEL_LENGTH) {
    return label;
  }
  const slice = label.slice(0, MAX_OPTION_LABEL_LENGTH);
  const lastSpace = slice.lastIndexOf(" ");
  return `${(lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trimEnd()}…`;
}

export function StateActivitiesField(props: DynamicFieldProps<FieldValues>) {
  const { control, sx, disabled } = props;
  const { label, selectOptions } = props.dynamicField;
  const name = props.name ?? `${props.namePrefix}.value`;
  const inputLabelId = `${name}-label`;

  const options = (selectOptions ?? []).map((opt) => ({
    value: opt.value,
    full: opt.label,
    display: truncateLabel(opt.label),
  }));

  const caption = <FormLabel sx={{ display: "block", mb: 2 }}>{CAPTION}</FormLabel>;

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    const value = formContext.getValues(name);
    const selected = Array.isArray(value) ? value : [];
    const text = options
      .filter((o) => selected.includes(o.value))
      .map((o) => o.full)
      .join(", ");
    return (
      <Box sx={sx}>
        {caption}
        <DisplayOnlyField text={text} label={label} />
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      {caption}
      <FormControl fullWidth>
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => {
            const selected: string[] = Array.isArray(field.value) ? field.value : [];
            return (
              <>
                <InputLabel id={inputLabelId} error={!!error}>
                  {label}
                </InputLabel>
                <Select
                  multiple
                  {...field}
                  value={selected}
                  labelId={inputLabelId}
                  label={label}
                  error={!!error}
                  disabled={disabled}
                  renderValue={(sel) =>
                    Array.isArray(sel) && sel.length ? `${sel.length} selected` : ""
                  }
                >
                  {options.map((opt) => {
                    const itemText = <ListItemText primary={opt.display} />;
                    return (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox checked={selected.includes(opt.value)} />
                        {opt.display !== opt.full ? (
                          // Long label is truncated; reveal the full text on hover.
                          <Tooltip title={opt.full} enterDelay={500} enterNextDelay={500}>
                            {itemText}
                          </Tooltip>
                        ) : (
                          itemText
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
                <ErrorDisplay error={error} />
              </>
            );
          }}
        />
      </FormControl>
    </Box>
  );
}
